/* Jaduzar — header behaviour and scroll reveals. No dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Document-level listeners must be bound once. Section elements are replaced
     on every Theme Editor save, but `document` is not. */
  var docBound = { nav: false, drops: false };

  /* ---------------------------------------------------------------------
     Mobile navigation
     --------------------------------------------------------------------- */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var panel = document.querySelector('[data-nav-panel]');
    if (!toggle || !panel) return;

    var openLabel = toggle.getAttribute('aria-label') || 'Open menu';
    var closeLabel = panel.getAttribute('data-close-label') || 'Close menu';
    var lastFocused = null;

    function isOpen() {
      return toggle.getAttribute('aria-expanded') === 'true';
    }

    function open() {
      lastFocused = document.activeElement;
      panel.hidden = false;
      // Force a reflow so the transition runs from the hidden state.
      void panel.offsetWidth;
      panel.classList.add('is-open');
      document.documentElement.classList.add('nav-open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', closeLabel);
      var first = panel.querySelector('a, button');
      if (first) first.focus({ preventScroll: true });
    }

    function close(returnFocus) {
      panel.classList.remove('is-open');
      document.documentElement.classList.remove('nav-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', openLabel);

      var finish = function () {
        if (!isOpen()) panel.hidden = true;
      };
      if (reduceMotion.matches) {
        finish();
      } else {
        window.setTimeout(finish, 260);
      }

      if (returnFocus) {
        (lastFocused && lastFocused.focus ? lastFocused : toggle).focus({ preventScroll: true });
      }
    }

    toggle.addEventListener('click', function () {
      isOpen() ? close(true) : open();
    });

    // Anchor links inside the panel should close it and let the browser scroll.
    panel.addEventListener('click', function (event) {
      if (event.target.closest('a')) close(false);
    });

    if (!docBound.nav) {
      docBound.nav = true;
      document.addEventListener('keydown', function (event) {
        var current = document.querySelector('[data-nav-toggle]');
        if (event.key === 'Escape' && current && current.getAttribute('aria-expanded') === 'true') {
          current.click();
        }
      });
    }

    // Keep focus inside the panel while it is open.
    // `summary` is in the list because the drawer's grouped items are
    // <details>, and their toggles must be reachable by keyboard.
    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !isOpen()) return;
      var focusable = panel.querySelectorAll('a[href], button:not([disabled]), summary');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Returning to a desktop width should never leave the panel stranded open.
    var desktop = window.matchMedia('(min-width: 821px)');
    var onDesktop = function (event) {
      if (event.matches && isOpen()) close(false);
    };
    desktop.addEventListener ? desktop.addEventListener('change', onDesktop) : desktop.addListener(onDesktop);
  }

  /* ---------------------------------------------------------------------
     Header gains weight once the hero is behind it
     --------------------------------------------------------------------- */
  function initNavScrollState() {
    var wrap = document.querySelector('.nav-wrap');
    if (!wrap) return;

    var ticking = false;
    function update() {
      wrap.classList.toggle('is-scrolled', window.scrollY > 40);
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------------------------------------------------------------------
     Scroll reveals — only for browsers without `animation-timeline: view()`.
     Content is visible by default; this only adds the entrance.
     --------------------------------------------------------------------- */
  function initReveals() {
    var supportsScrollTimeline =
      window.CSS && CSS.supports && CSS.supports('animation-timeline', 'view()');
    if (supportsScrollTimeline || reduceMotion.matches) return;
    if (!('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll('.reveal, .reveal-s > *');
    if (!targets.length) return;

    document.documentElement.classList.add('js-reveal');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Counters in the companies strip and anywhere else with [data-count-to]
     --------------------------------------------------------------------- */
  function initCounters() {
    var nodes = document.querySelectorAll('[data-count-to]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          observer.unobserve(el);

          var target = parseFloat(el.getAttribute('data-count-to'));
          if (isNaN(target)) return;
          if (reduceMotion.matches) {
            el.textContent = String(target);
            return;
          }

          var duration = 1100;
          var start = null;
          function step(now) {
            if (start === null) start = now;
            var progress = Math.min((now - start) / duration, 1);
            // ease-out-quart
            var eased = 1 - Math.pow(1 - progress, 4);
            el.textContent = String(Math.round(target * eased));
            if (progress < 1) window.requestAnimationFrame(step);
          }
          window.requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );

    nodes.forEach(function (el) {
      observer.observe(el);
    });
  }

  function closeEveryDropdown() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nav-drop]'), function (item) {
      var toggle = item.querySelector('[data-nav-drop-toggle]');
      var panel = item.querySelector('.nav-drop');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }

  /* ---------------------------------------------------------------------
     Header dropdowns. Click rather than hover, so keyboard and touch get the
     same behaviour as a mouse.
     --------------------------------------------------------------------- */
  function initNavDropdowns() {
    var items = document.querySelectorAll('[data-nav-drop]');
    if (!items.length) return;

    var all = [];

    Array.prototype.forEach.call(items, function (item) {
      var toggle = item.querySelector('[data-nav-drop-toggle]');
      var panel = item.querySelector('.nav-drop');
      if (!toggle || !panel) return;

      all.push({ toggle: toggle, panel: panel });

      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        closeEveryDropdown();
        if (!open) {
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });

      /* On a pointer device, hovering the group opens it as well. */
      item.addEventListener('mouseenter', function () {
        if (window.matchMedia('(hover: hover)').matches) {
          closeEveryDropdown();
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
      item.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: hover)').matches) closeEveryDropdown();
      });
    });

    if (!docBound.drops) {
      docBound.drops = true;

      document.addEventListener('click', function (event) {
        if (event.target.closest('[data-nav-drop]')) return;
        closeEveryDropdown();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        var open = document.querySelector('[data-nav-drop-toggle][aria-expanded="true"]');
        if (!open) return;
        closeEveryDropdown();
        open.focus();
      });
    }

    /* Tabbing out of a group closes it, so focus never lands behind a panel. */
    Array.prototype.forEach.call(items, function (item) {
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) {
          var toggle = item.querySelector('[data-nav-drop-toggle]');
          var panel = item.querySelector('.nav-drop');
          if (toggle && panel) {
            toggle.setAttribute('aria-expanded', 'false');
            panel.hidden = true;
          }
        }
      });
    });
  }

  function init() {
    /* The Theme Editor fires shopify:section:load on every save. Without this
       guard each save would stack another set of listeners on the same nodes. */
    if (document.documentElement.getAttribute('data-theme-init') !== 'true') {
      document.documentElement.setAttribute('data-theme-init', 'true');
      initNavScrollState();
      initReveals();
    }
    initNav();
    initNavDropdowns();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', init);
})();
